// Styled components for Minew Testing Tool

import styled from 'styled-components';

export const Content = styled.div`
  padding: 1.5rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

export const Header = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

export const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StatusBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const StatusItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
  opacity: 0.8;
`;

export const ControlsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

interface ButtonProps {
  primary?: boolean;
  danger?: boolean;
}

export const Button = styled.button<ButtonProps>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  background: ${props => (props.primary ? '#2196F3' : props.danger ? '#f44336' : 'rgba(255, 255, 255, 0.1)')};
  color: white;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  width: 80px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

export const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
`;

export const PanelTitle = styled.h3`
  margin: 0 0 1rem 0;
`;

export const BeaconListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

interface BeaconItemContainerProps {
  active?: boolean;
  color?: string;
}

export const BeaconItemContainer = styled.div<BeaconItemContainerProps>`
  padding: 0.75rem;
  background: ${props => (props.active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.05)')};
  border-radius: 4px;
  border-left: 3px solid ${props => props.color || '#666'};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

export const BeaconId = styled.div`
  font-family: monospace;
  font-size: 0.75rem;
  opacity: 0.6;
  margin-top: 0.25rem;
`;

interface BeaconLocationProps {
  offsite?: boolean;
}

export const BeaconLocationText = styled.div<BeaconLocationProps>`
  font-size: 0.8rem;
  color: ${props => (props.offsite ? '#f44336' : '#4CAF50')};
  margin-top: 0.25rem;
`;

export const PatientAssignment = styled.div`
  font-size: 0.75rem;
  color: #4caf50;
  margin-top: 0.25rem;
`;

export const RoomTypeSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const RoomTypeHeader = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.9;
`;

export const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`;

interface RoomCardContainerProps {
  hasGateway?: boolean;
  clickable?: boolean;
}

export const RoomCardContainer = styled.div<RoomCardContainerProps>`
  padding: 0.75rem;
  background: ${props => (props.hasGateway ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)')};
  border: 1px solid ${props => (props.hasGateway ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 4px;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #2196f3;
  }
`;

export const RoomName = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

export const GatewayInfo = styled.div`
  font-size: 0.75rem;
  font-family: monospace;
  opacity: 0.7;
`;

export const BeaconsInRoom = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

interface BeaconDotProps {
  color?: string;
}

export const BeaconDot = styled.div<BeaconDotProps>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
`;

interface InfoBoxContainerProps {
  variant?: 'info' | 'success' | 'error';
}

export const InfoBoxContainer = styled.div<InfoBoxContainerProps>`
  padding: 1rem;
  background: ${props => {
    switch (props.variant) {
      case 'success':
        return 'rgba(76, 175, 80, 0.1)';
      case 'error':
        return 'rgba(244, 67, 54, 0.1)';
      default:
        return 'rgba(33, 150, 243, 0.1)';
    }
  }};
  border: 1px solid
    ${props => {
      switch (props.variant) {
        case 'success':
          return 'rgba(76, 175, 80, 0.3)';
        case 'error':
          return 'rgba(244, 67, 54, 0.3)';
        default:
          return 'rgba(33, 150, 243, 0.3)';
      }
    }};
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
`;

export const WarningBoxContainer = styled.div`
  padding: 0.75rem;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
